import { createElement } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { createTheme } from "@mui/material/styles";

const APP_ENABLED_BORDER_COLOR = "rgba(255, 255, 255, 0.42)";
const APP_ELEVATED_ACTION_SHADOW = "0px 1px 5px 0px rgba(0,0,0,0.12), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.2)";
const APP_SCRIM_COLOR = "rgba(0,0,0,0.5)";
const APP_STRONG_SCRIM_COLOR = "rgba(0,0,0,0.7)";
const APP_SELECTION_ACCENT_COLOR = "#00bfff";
const APP_SELECTION_ACCENT_SHADOW = `0 0 12px ${APP_SELECTION_ACCENT_COLOR}`;
const APP_PILL_BORDER_RADIUS = "1000px";
const APP_ACTIVE_TOOL_COLOR = "#64B6FF";
const APP_CONTAINED_PRIMARY_TEXT_COLOR = "rgba(0, 0, 0, 0.87)";

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      light: "#37A2FF",
      main: "#1E88E5",
      contrastText: "rgba(255, 255, 255, 0.87)"
    },
    secondary: {
      light: "#F3E5F5",
      main: "#CE93D8",
      dark: "#AB47BC",
      contrastText: "rgba(0, 0, 0, 0.87)"
    },
    action: {
      active: "rgba(255, 255, 255, 0.56)",
      hover: "rgba(255, 255, 255, 0.08)",
      selected: "rgba(255, 255, 255, 0.16)",
      disabled: "rgba(255, 255, 255, 0.3)",
      disabledBackground: "rgba(255, 255, 255, 0.12)",
      focus: "rgba(255, 255, 255, 0.12)"
    },
    text: {
      primary: "#FFFFFF",
      secondary: "rgba(255, 255, 255, 0.7)",
      disabled: "rgba(255, 255, 255, 0.5)"
    },
    background: {
      default: "#121212",
      paper: "#121212"
    },
    success: {
      light: "#81C784",
      main: "#66BB6A",
      dark: "#388E3C",
      contrastText: "rgba(0, 0, 0, 0.87)"
    },
    error: {
      light: "#E57373",
      main: "#F44336",
      dark: "#D32F2F",
      contrastText: "#FFFFFF"
    },
    warning: {
      light: "#FFB74D",
      main: "#FFA726",
      dark: "#F57C00",
      contrastText: "rgba(0, 0, 0, 0.87)"
    },
    info: {
      light: "#90DDFE",
      main: "#7BBFDC",
      dark: "#64A7C3",
      contrastText: "rgba(0, 0, 0, 0.87)"
    },
    divider: "rgba(255, 255, 255, 0.12)"
  },
  typography: {
    fontFamily: ["proxima-nova", "sans-serif"].join(",")
  },
  components: {
    MuiAutocomplete: {
      defaultProps: {
        clearIcon: createElement(CloseIcon, { fontSize: "small" })
      }
    },
    MuiTooltip: {
      defaultProps: {
        enterDelay: 500,
        enterNextDelay: 500
      }
    },
    MuiSelect: {
      styleOverrides: {
        // Adds extra right padding so text doesn't collide with the dropdown icon.
        select({ theme: muiTheme }) {
          return {
            // Increase specificity without `!important` so this wins reliably.
            "&&": {
              paddingRight: "2.25rem",
              color: muiTheme.palette.text.secondary,
              fontSize: 12,
              letterSpacing: 0.4
            }
          };
        },
        icon({ theme: muiTheme }) {
          return {
            "&&": {
              color: muiTheme.palette.text.secondary
            }
          };
        }
      }
    },
    MuiRating: {
      styleOverrides: {
        iconActive: {
          color: "#FFB400"
        }
      }
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent"
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root({ ownerState }) {
          if (ownerState.variant !== "filled") {
            return {};
          }

          const base = {
            color: "rgba(255, 255, 255, 0.95)" as const
          };

          switch (ownerState.severity) {
            case "info":
              return {
                ...base,
                backgroundColor: "#1e3a4a",
                "& .MuiAlert-icon": { color: "#90DDFE" }
              };
            case "success":
              return {
                ...base,
                backgroundColor: "#1b3d2f",
                "& .MuiAlert-icon": { color: "#81C784" }
              };
            case "error":
              return {
                ...base,
                backgroundColor: "#3d1f1f",
                "& .MuiAlert-icon": { color: "#E57373" }
              };
            case "warning":
              return {
                ...base,
                backgroundColor: "#3d3520",
                "& .MuiAlert-icon": { color: "#FFB74D" }
              };
            default:
              return {};
          }
        }
      }
    },
    MuiInput: {
      styleOverrides: {
        root({ theme: muiTheme }) {
          const underlineBeforeSelector = "&::before";
          const underlineHoverBeforeSelector = "&:hover:not(.Mui-disabled, .Mui-error)::before";
          const selectUnderlineBeforeSelector = "&.MuiSelect-root::before";
          const selectUnderlineHoverBeforeSelector = "&.MuiSelect-root:hover:not(.Mui-disabled, .Mui-error)::before";
          return {
            [underlineBeforeSelector]: {
              borderBottomColor: "rgba(255, 255, 255, 0.42)"
            },
            // Default underline hover for inputs (keep brighter).
            [underlineHoverBeforeSelector]: {
              borderBottomColor: muiTheme.palette.text.primary
            },
            // For Select (standard variant), keep hover underline at text.secondary (avoid white flash).
            [selectUnderlineBeforeSelector]: {
              borderBottomColor: muiTheme.palette.text.secondary
            },
            [selectUnderlineHoverBeforeSelector]: {
              borderBottomColor: muiTheme.palette.text.secondary
            }
          };
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: "#F44336"
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&:autofill": {
            boxShadow: "0 0 0 100px rgba(0, 0, 0, 0.87) inset !important"
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root({ theme: muiTheme }) {
          return {
            borderColor: muiTheme.palette.divider
          };
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        outlined({ theme: muiTheme }) {
          const hoverSelector = "&:hover";
          return {
            borderColor: muiTheme.palette.divider,
            [hoverSelector]: {
              borderColor: muiTheme.palette.divider
            }
          };
        }
      }
    }
  }
});

export {
  APP_ACTIVE_TOOL_COLOR,
  APP_CONTAINED_PRIMARY_TEXT_COLOR,
  APP_ENABLED_BORDER_COLOR,
  APP_ELEVATED_ACTION_SHADOW,
  APP_PILL_BORDER_RADIUS,
  APP_SCRIM_COLOR,
  APP_SELECTION_ACCENT_COLOR,
  APP_SELECTION_ACCENT_SHADOW,
  APP_STRONG_SCRIM_COLOR
};

export default theme;
